import type { Bi } from 'schools/swaraVigyan/types/bilingual';

export type SwaraTopic = {
  id: string;
  title: Bi;
  summary: Bi;
  readMinutes: number;
  icon: string;
};

export type SwaraChapter = {
  id: string;
  number: number;
  title: Bi;
  subtitle: Bi;
  icon: string;
  topics: SwaraTopic[];
};

export const SWARA_HERO = {
  eyebrow: { en: 'Breath · Daily timing', hi: 'श्वास · दैनिक समय' },
  titleShort: { en: 'Swara Vigyan', hi: 'स्वर विज्ञान' },
  title: { en: 'Swara Vigyan', hi: 'स्वर विज्ञान' },
  motto: {
    en: 'Learn which nostril is active, how it changes through the day, and when to do what.',
    hi: 'जानें कौन-सी नासिका सक्रिय है, दिन भर यह कैसे बदलती है, और क्या कब करें।',
  },
  tagline: {
    en: 'Fifty-six lessons with a breath picture on every page - left, right, moon days, and daily timing.',
    hi: 'छप्पन पाठ - हर पृष्ठ पर श्वास चित्र: बाएँ, दाएँ, चंद्र दिन और दैनिक समय।',
  },
  intro: {
    en: 'Pair with Panchang for moon day and star - this school teaches breath-based daily choices in plain language.',
    hi: 'चंद्र दिन और नक्षत्र हेतु पंचांग के साथ - यह स्कूल सरल भाषा में श्वास-आधारित दैनिक निर्णय सिखाता है।',
  },
  trait: {
    en: 'Active nostril + moon day + time = better daily choices',
    hi: 'सक्रिय नासिका + चंद्र दिन + समय = बेहतर दैनिक निर्णय',
  },
};

function topic(
  id: string,
  titleEn: string,
  titleHi: string,
  summaryEn: string,
  summaryHi: string,
  readMinutes: number,
  icon: string,
): SwaraTopic {
  return {
    id,
    title: { en: titleEn, hi: titleHi },
    summary: { en: summaryEn, hi: summaryHi },
    readMinutes,
    icon,
  };
}

export const SWARA_CHAPTERS: SwaraChapter[] = [
  {
    id: 'introduction',
    number: 1,
    title: { en: 'Start here', hi: 'शुरुआत' },
    subtitle: {
      en: 'What Swara Vigyan is and how it links to Panchang',
      hi: 'स्वर विज्ञान क्या है और पंचांग से कैसे जुड़ता है',
    },
    icon: '🌬️',
    topics: [
      topic(
        'what-is-swara-vigyan',
        'What is Swara Vigyan?',
        'स्वर विज्ञान क्या है?',
        'Watch which nostril you breathe through and use that for daily timing.',
        'देखें किस नासिका से साँस ले रहे हैं और दैनिक समय के लिए उपयोग करें।',
        11,
        '🌬️',
      ),
      topic(
        'shiva-swarodaya-tradition',
        'The old book behind it',
        'पीछे की परंपरा',
        'Shiva Swarodaya - the main source text, kept simple.',
        'शिव स्वरोदय - मुख्य ग्रंथ, सरल भाषा में।',
        12,
        '📜',
      ),
      topic(
        'swara-vs-pranayama',
        'Watching breath vs controlling breath',
        'श्वास देखना बनाम नियंत्रण',
        'Swara = notice and pick timing. Pranayama = breath exercises. Different jobs.',
        'स्वर = देखें और समय चुनें। प्राणायाम = श्वास अभ्यास। अलग काम।',
        10,
        '⚖️',
      ),
      topic(
        'bridge-from-panchang',
        'Link to Panchang',
        'पंचांग से जुड़ाव',
        'Moon day, weekday, and star name help read breath rules.',
        'चंद्र दिन, वार और नक्षत्र श्वास नियम पढ़ने में मदद करते हैं।',
        12,
        '🔗',
      ),
      topic(
        'swara-ethics-overview',
        'Watch breath, don’t diagnose',
        'श्वास देखें, रोग न थहराएँ',
        'Swara is daily timing awareness - not a medical test or fortune telling.',
        'स्वर दैनिक समय की जागरूकता है - चिकित्सा परीक्षा या भविष्यवाणी नहीं।',
        11,
        '⚕️',
      ),
    ],
  },
  {
    id: 'three-nostrils',
    number: 2,
    title: { en: 'Left, right, and middle', hi: 'बाएँ, दाएँ और बीच' },
    subtitle: {
      en: 'Ida, Pingala, Sushumna and the five elements',
      hi: 'इडा, पिंगला, सुषुम्ना और पाँच तत्व',
    },
    icon: '🌙',
    topics: [
      topic(
        'pancha-tattva-and-breath',
        'Five elements and breath',
        'पाँच तत्व और श्वास',
        'Earth, water, fire, air, space - how breath can feel cool or warm.',
        'पृथ्वी, जल, अग्नि, वायु, आकाश - श्वास ठंडा या गर्म कैसे लगता है।',
        12,
        '🌀',
      ),
      topic(
        'ida-chandra-nadi',
        'Left nostril (moon side)',
        'बाईं नासिका (चंद्र पक्ष)',
        'Cooler, calmer - good for study, rest, and quiet work.',
        'ठंडी, शांत - पढ़ाई, विश्राम और शांत काम हेतु।',
        11,
        '🌙',
      ),
      topic(
        'pingala-surya-nadi',
        'Right nostril (sun side)',
        'दाईं नासिका (सूर्य पक्ष)',
        'Warmer, more active - good for exercise, food, and bold work.',
        'गर्म, सक्रिय - व्यायाम, भोजन और ठोस काम हेतु।',
        11,
        '☀️',
      ),
      topic(
        'sushumna-central-channel',
        'Both nostrils open',
        'दोनों नासिकाएँ खुली',
        'Short window for prayer - avoid starting big worldly tasks.',
        'प्रार्थना का छोटा समय - बड़े सांसारिक काम शुरू न करें।',
        12,
        '🕉️',
      ),
      topic(
        'nadi-system-body-map',
        'Channels in the body',
        'शरीर में नाड़ी',
        'Left, right, and middle paths - simple body map.',
        'बाएँ, दाएँ और बीच की नाड़ी - सरल शरीर मानचित्र।',
        13,
        '🧘',
      ),
      topic(
        'blocked-nostril-caution',
        'When one side feels blocked',
        'जब एक ओर बंद लगे',
        'Cold or allergy can block a nostril - note it, don’t treat swara as illness.',
        'सर्दी या एलर्जी नासिका बंद कर सकती है - लिखें, स्वर को रोग न समझें।',
        11,
        '🤧',
      ),
      topic(
        'breath-feel-vs-facts',
        'Feeling vs table rules',
        'अनुभव बनाम सारणी नियम',
        'Your felt breath can differ from the book - log both and learn your pattern.',
        'महसूस श्वास ग्रंथ से अलग हो सकता है - दोनों लिखें, अपना पैटर्न सीखें।',
        12,
        '📊',
      ),
    ],
  },
  {
    id: 'observation',
    number: 3,
    title: { en: 'How to check your breath', hi: 'श्वास कैसे जाँचें' },
    subtitle: {
      en: 'Hand check, daily rhythm, and a simple diary',
      hi: 'हाथ से जाँच, दैनिक लय और सरल डायरी',
    },
    icon: '✋',
    topics: [
      topic(
        'how-to-check-swara',
        'How to check',
        'कैसे जाँचें',
        'Palm under nose - feel which side has more air.',
        'हथेली नाक के नीचे - किस ओर ज़्यादा हवा महसूस हो।',
        12,
        '✋',
      ),
      topic(
        'swara-cycle-rhythm',
        'The daily rhythm',
        'दैनिक लय',
        'Breath side usually changes about every 90 minutes.',
        'श्वास की ओर आमतौर पर लगभग 90 मिनट में बदलती है।',
        11,
        '⏱️',
      ),
      topic(
        'swara-transitions',
        'When it switches',
        'जब बदलाव हो',
        'Brief pause when moving left to right - don’t rush big choices then.',
        'बाएँ से दाएँ जाते समय छोटा विराम - तब बड़े निर्णय न लें।',
        12,
        '🔄',
      ),
      topic(
        'observation-journal',
        'Keep a simple log',
        'सरल लेख रखें',
        'Write time + left/right/both for a week - see your pattern.',
        'सप्ताह भर समय + बाएँ/दाएँ/दोनों लिखें - अपना पैटर्न देखें।',
        13,
        '📓',
      ),
      topic(
        'hand-check-mistakes',
        'Hand-check mistakes',
        'हाथ जाँच की गलतियाँ',
        'Too hard breathing, wrong angle, or checking right after food skews results.',
        'ज़ोर से साँस, गलत कोण, या भोजन के तुरंत बाद जाँच परिणाम बिगाड़ती है।',
        11,
        '❌',
      ),
      topic(
        'seven-day-log-review',
        'Review your week log',
        'सप्ताह लेख की समीक्षा',
        'After seven days, spot your usual switch times and mismatches.',
        'सात दिन बाद, अपने बदलाव समय और असंगति देखें।',
        12,
        '📈',
      ),
    ],
  },
  {
    id: 'lunar-rules',
    number: 4,
    title: { en: 'Moon days and sunrise breath', hi: 'चंद्र दिन और सूर्योदय श्वास' },
    subtitle: {
      en: 'Bright and dark half patterns, today’s check, gentle shifts',
      hi: 'शुक्ल-कृष्ण पक्ष, आज की जाँच, कोमल बदलाव',
    },
    icon: '🌅',
    topics: [
      topic(
        'sunrise-swara-principle',
        'Why sunrise matters',
        'सूर्योदय क्यों महत्वपूर्ण',
        'Many rules start with: which nostril at sunrise on this moon day?',
        'कई नियम शुरू होते हैं: इस चंद्र दिन पर सूर्योदय पर कौन-सी नासिका?',
        12,
        '🌅',
      ),
      topic(
        'shukla-paksha-swara-pattern',
        'Bright half of the month',
        'महीने का शुक्ल पक्ष',
        'Pattern for days 1–15 after new moon.',
        'अमावस्या के बाद दिन 1–15 का पैटर्न।',
        13,
        '🌒',
      ),
      topic(
        'krishna-paksha-swara-pattern',
        'Dark half of the month',
        'महीने का कृष्ण पक्ष',
        'Pattern for days 1–15 after full moon (reversed).',
        'पूर्णिमा के बाद दिन 1–15 का पैटर्न (उलटा)।',
        13,
        '🌘',
      ),
      topic(
        'live-tithi-swara-today',
        "Today's likely nostril",
        'आज की संभावित नासिका',
        'App reads today’s moon day and shows expected side at sunrise.',
        'ऐप आज का चंद्र दिन पढ़कर सूर्योदय पर संभावित ओर दिखाता है।',
        14,
        '📅',
      ),
      topic(
        'changing-active-swara',
        'If you want to shift side',
        'ओर बदलना चाहें तो',
        'Traditional gentle methods - not medical treatment.',
        'परंपरागत कोमल तरीके - चिकित्सा नहीं।',
        12,
        '🔀',
      ),
      topic(
        'amavasya-purnima-swara',
        'New moon and full moon days',
        'अमावस्या और पूर्णिमा',
        'Special caution on tithi 15 - both nostrils may feel open; pray, don’t rush deals.',
        'तिथि 15 पर विशेष सावधानी - दोनों नासिकाएँ खुली लग सकती हैं; प्रार्थना करें, सौदे न करें।',
        12,
        '🌕',
      ),
      topic(
        'month-at-a-glance',
        'Month at a glance',
        'महीना एक नज़र में',
        'See bright and dark half sunrise nostril pattern on one grid.',
        'एक जाल में शुक्ल-कृष्ण सूर्योदय नासिका पैटर्न देखें।',
        13,
        '🗓️',
      ),
    ],
  },
  {
    id: 'daily-action',
    number: 5,
    title: { en: 'What to do when', hi: 'कब क्या करें' },
    subtitle: {
      en: 'Do and don’t lists, daily plan, travel direction',
      hi: 'करें-न करें सूची, दिन योजना, यात्रा दिशा',
    },
    icon: '📋',
    topics: [
      topic(
        'activities-in-ida',
        'When left nostril is active',
        'जब बाईं नासिका सक्रिय',
        'Study, travel, calm talks, and new learning.',
        'पढ़ाई, यात्रा, शांत बातचीत और नई सीख।',
        12,
        '📚',
      ),
      topic(
        'activities-in-pingala',
        'When right nostril is active',
        'जब दाईं नासिका सक्रिय',
        'Food, exercise, hard work, and sleep.',
        'भोजन, व्यायाम, कठिन काम और नींद।',
        12,
        '💪',
      ),
      topic(
        'activities-in-sushumna',
        'When both are open',
        'जब दोनों खुली हों',
        'Prayer and meditation - skip starting deals or fights.',
        'प्रार्थना और ध्यान - सौदे या झगड़े शुरू न करें।',
        11,
        '🪔',
      ),
      topic(
        'food-sleep-daily-routine',
        'Food, sleep, daily plan',
        'भोजन, नींद, दिन योजना',
        'When to eat, rest, and work through the day.',
        'दिन भर कब खाएँ, आराम करें और काम करें।',
        13,
        '🍽️',
      ),
      topic(
        'direction-and-travel',
        'Direction for travel',
        'यात्रा की दिशा',
        'Which direction to face or leave - based on active nostril.',
        'किस दिशा में मुँह करें या निकलें - सक्रिय नासिका के अनुसार।',
        13,
        '🧭',
      ),
      topic(
        'meetings-and-deals-timing',
        'Meetings and deals',
        'बैठक और सौदे',
        'Calm talks in left (moon) flow; bold signing in right (sun) flow.',
        'बाईं (चंद्र) प्रवाह में शांत बात; दाईं (सूर्य) प्रवाह में ठोस हस्ताक्षर।',
        12,
        '🤝',
      ),
      topic(
        'sleep-wind-down-routine',
        'Sleep wind-down',
        'नींद की तैयारी',
        'Shift to right (sun) nostril before sleep; avoid screens in both-nostril pause.',
        'नींद से पहले दाईं (सूर्य) नासिका; दोनों-नासिका विराम में स्क्रीन नहीं।',
        12,
        '🌙',
      ),
    ],
  },
  {
    id: 'advanced-practice',
    number: 6,
    title: { en: 'Stars, weekdays, and practice', hi: 'नक्षत्र, वार और अभ्यास' },
    subtitle: {
      en: 'Nakshatra wheel, weekday chart, health note, verses, capstone',
      hi: 'नक्षत्र चक्र, वार चार्ट, स्वास्थ्य नोट, श्लोक, समापन',
    },
    icon: '⭐',
    topics: [
      topic(
        'nakshatra-and-swara',
        'Moon mansions and breath',
        'नक्षत्र और श्वास',
        '27 star groups - short tips for each.',
        '27 नक्षत्र - प्रत्येक के लिए छोटे सुझाव।',
        14,
        '⭐',
      ),
      topic(
        'vara-weekday-swara',
        'Weekdays and breath',
        'वार और श्वास',
        'Sunday to Saturday - how the day’s planet fits breath.',
        'रवि से शनि - दिन के ग्रह का श्वास से संबंध।',
        12,
        '🗓️',
      ),
      topic(
        'swara-and-health',
        'Breath and body (awareness)',
        'श्वास और शरीर (जागरूकता)',
        'Old texts link nostril side to body areas - awareness only, not medical advice.',
        'पुराने ग्रंथ नासिका को शरीर से जोड़ते हैं - केवल जागरूकता, चिकित्सा नहीं।',
        12,
        '❤️',
      ),
      topic(
        'shiva-swarodaya-shloka-study',
        'Read a few key verses',
        'कुछ मुख्य श्लोक',
        'Short verses in Hindi and English with plain meaning.',
        'हिंदी और अंग्रेज़ी में छोटे श्लोक सरल अर्थ के साथ।',
        14,
        '📖',
      ),
      topic(
        'capstone-daily-swara-planner',
        'Plan your whole day',
        'पूरा दिन योजना',
        'Check breath → match moon day → pick actions - practice lesson.',
        'श्वास जाँचें → चंद्र दिन मिलाएँ → कर्म चुनें - अभ्यास पाठ।',
        16,
        '🎯',
      ),
      topic(
        'nakshatra-pada-lore',
        'Star quarters (pada)',
        'नक्षत्र पाद कथा',
        'Each star has four steps - tradition adds fine tips; start with the star wheel.',
        'प्रत्येक नक्षत्र के चार पाद - परंपरा में बारीक सुझाव; नक्षत्र चक्र से शुरू करें।',
        13,
        '🔯',
      ),
    ],
  },
  {
    id: 'muhurta-bridge',
    number: 7,
    title: { en: 'Muhurta & Panchang', hi: 'मुहूर्त और पंचांग' },
    subtitle: {
      en: 'Abhijit, Rahu Kaal, festivals, and seasonal rhythm',
      hi: 'अभिजीत, राहु काल, त्योहार और ऋतु लय',
    },
    icon: '⏰',
    topics: [
      topic(
        'muhurta-basics-swara',
        'Muhurta meets swara',
        'मुहूर्त और स्वर',
        'Good time in Panchang plus matching nostril makes a stronger start.',
        'पंचांग का शुभ समय और मेल खाती नासिका मजबूत शुरुआत देती है।',
        13,
        '⏰',
      ),
      topic(
        'abhijit-window',
        'Abhijit window',
        'अभिजीत समय',
        'Midday correction slot - check breath before important work.',
        'मध्यदिन सुधार खिड़की - महत्वपूर्ण काम से पहले श्वास जाँचें।',
        12,
        '☀️',
      ),
      topic(
        'rahu-kaal-caution',
        'Rahu Kaal caution',
        'राहु काल सावधानी',
        'Avoid starting risky travel or deals in Rahu Kaal - log your nostril anyway.',
        'राहु काल में जोखिम यात्रा या सौदा न शुरू करें - फिर भी नासिका लिखें।',
        12,
        '🌑',
      ),
      topic(
        'festival-day-check',
        'Festival day check',
        'त्योहार दिन जाँच',
        'On festival mornings, note sunrise nostril and Panchang tithi together.',
        'त्योहार सुबह सूर्योदय नासिका और पंचांग तिथि एक साथ लिखें।',
        13,
        '🪔',
      ),
      topic(
        'seasonal-rhythm-monsoon',
        'Seasonal rhythm',
        'ऋतु की लय',
        'Monsoon and heat change how nostrils feel - adjust expectations, keep logging.',
        'मानसून और गर्मी नासिका अनुभव बदलती है - अपेक्षा समायोजित करें, लेख जारी रखें।',
        12,
        '🌧️',
      ),
      topic(
        'yoga-karana-note',
        'Yoga and Karana note',
        'योग और करण नोट',
        'Panchang limbs add context - swara still starts with your hand check.',
        'पंचांग अंग संदर्भ देते हैं - स्वर फिर भी हाथ जाँच से शुरू।',
        12,
        '📿',
      ),
    ],
  },
  {
    id: 'practice-ethics',
    number: 8,
    title: { en: 'Practice & ethics', hi: 'अभ्यास और नैतिकता' },
    subtitle: {
      en: 'Not medicine, client notes, family teaching, troubleshooting',
      hi: 'चिकित्सा नहीं, ग्राहक नोट, परिवार सिखाना, समस्या सुलझाना',
    },
    icon: '📋',
    topics: [
      topic(
        'ethics-not-medicine',
        'Ethics: not medicine',
        'नैतिकता: चिकित्सा नहीं',
        'Never tell someone they are sick because of nostril side.',
        'नासिका ओर से कभी न कहें कि कोई बीमार है।',
        12,
        '⚕️',
      ),
      topic(
        'three-step-morning-read',
        'Three-step morning read',
        'तीन-चरण सुबह पढ़ना',
        'Panchang tithi → sunrise rule → hand check - in that order.',
        'पंचांग तिथि → सूर्योदय नियम → हाथ जाँच - इसी क्रम में।',
        13,
        '🌅',
      ),
      topic(
        'writing-client-notes',
        'Writing client notes',
        'ग्राहक नोट लेखन',
        'Record felt nostril, tithi, and planned action - no medical claims.',
        'महसूस नासिका, तिथि और योजित कर्म लिखें - चिकित्सा दावा नहीं।',
        12,
        '✍️',
      ),
      topic(
        'teaching-family-routine',
        'Teaching family',
        'परिवार को सिखाना',
        'One-minute hand check at breakfast - make it a game, not fear.',
        'नाश्ते पर एक मिनट हाथ जाँच - खेल बनाएँ, डर नहीं।',
        12,
        '👨‍👩‍👧',
      ),
      topic(
        'mismatch-troubleshooting',
        'When rules don’t match',
        'जब नियम न मिलें',
        'Table says left, you feel right - log it, rest, recheck after an hour.',
        'सारणी बाएँ कहे, आप दाएँ महसूस करें - लिखें, आराम, एक घंटे बाद फिर जाँचें।',
        13,
        '🔧',
      ),
      topic(
        'week-review-workflow',
        'Week review workflow',
        'सप्ताह समीक्षा क्रम',
        'Sunday evening: scan log, note patterns, plan next week’s gentle shifts.',
        'रविवार शाम: लेख देखें, पैटर्न लिखें, अगले सप्ताह की कोमल योजना।',
        14,
        '📊',
      ),
    ],
  },
  {
    id: 'life-in-practice',
    number: 9,
    title: { en: 'Swara in daily life', hi: 'दैनिक जीवन में स्वर' },
    subtitle: {
      en: 'Office, travel, partner practice, small spaces, capstone workshop',
      hi: 'कार्यालय, यात्रा, साझा अभ्यास, छोटा स्थान, समापन कार्यशाला',
    },
    icon: '🏠',
    topics: [
      topic(
        'office-day-routine',
        'Office day routine',
        'कार्यालय दिन क्रम',
        'Match meetings to nostril - calm reviews on left, pitches on right.',
        'बैठक नासिका से मिलाएँ - बाएँ पर शांत समीक्षा, दाएँ पर प्रस्तुति।',
        13,
        '💼',
      ),
      topic(
        'travel-day-checklist',
        'Travel day checklist',
        'यात्रा दिन सूची',
        'Check nostril, compass direction, and Panchang before leaving.',
        'निकलने से पहले नासिका, दिशा कम्पास और पंचांग जाँचें।',
        14,
        '✈️',
      ),
      topic(
        'shared-practice-partner',
        'Practice with a partner',
        'साझी अभ्यास साथी',
        'Compare hand checks at the same time - learn if you sync or differ.',
        'एक समय हाथ जाँच तुलना करें - मेल है या अंतर, सीखें।',
        12,
        '👥',
      ),
      topic(
        'small-space-habits',
        'Small space habits',
        'छोटे स्थान की आदतें',
        'Desk, car, or kitchen - same one-minute check works anywhere.',
        'मेज, कार या रसोई - वही एक मिनट जाँच कहीं भी चलती है।',
        11,
        '🏠',
      ),
      topic(
        'pre-journey-plan',
        'Pre-journey plan',
        'यात्रा पूर्व योजना',
        'Night before: note tomorrow’s tithi and likely sunrise nostril.',
        'पूर्व रात: कल की तिथि और संभावित सूर्योदय नासिका लिखें।',
        12,
        '🗺️',
      ),
      topic(
        'capstone-seven-day-workshop',
        'Seven-day workshop',
        'सात दिन कार्यशाला',
        'Full week: log, review, plan - graduate with your own swara routine.',
        'पूरा सप्ताह: लेख, समीक्षा, योजना - अपना स्वर क्रम लेकर निकलें।',
        16,
        '🎓',
      ),
    ],
  },
];

export const SWARA_TOPIC_IDS = SWARA_CHAPTERS.flatMap((c) => c.topics.map((t) => t.id));

export function getSwaraTopic(id: string) {
  for (const chapter of SWARA_CHAPTERS) {
    const t = chapter.topics.find((x) => x.id === id);
    if (t) return { topic: t, chapter };
  }
  return null;
}

export function getSwaraChapter(chapterId: string): SwaraChapter | undefined {
  return SWARA_CHAPTERS.find((c) => c.id === chapterId);
}

export function getSwaraChapterIndex(chapterId: string): number {
  return SWARA_CHAPTERS.findIndex((c) => c.id === chapterId);
}

export function getSwaraTopicCount(): number {
  return SWARA_CHAPTERS.reduce((n, c) => n + c.topics.length, 0);
}

export function getSwaraTopicNeighbors(topicId: string): {
  prev: SwaraTopic | null;
  next: SwaraTopic | null;
} {
  const flat = SWARA_CHAPTERS.flatMap((c) => c.topics);
  const idx = flat.findIndex((t) => t.id === topicId);
  if (idx < 0) return { prev: null, next: null };
  return { prev: flat[idx - 1] ?? null, next: flat[idx + 1] ?? null };
}
